import { faX } from '@fortawesome/pro-regular-svg-icons';
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { Alert } from 'src/components/Alert';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { File, FileDropzone } from 'src/components/Uploader/FileDropzone';
import { FileIcon } from 'src/components/Uploader/FileIcon';
import {
  ImageDimensionRequirements,
  extractDimensionsFromCaption,
  validateImageDimensions,
} from 'src/utils/imageValidation';
import toast from 'src/utils/toast';


// Define and export common accept types using PascalCase
export const SafeDefaultAcceptTypes: Record<string, string[]> = {
  'image/jpeg': [],
  'image/png': [],
  'image/gif': [],
  'image/webp': [],
  'application/pdf': [],
};

export const ImageOrDocTypes: Record<string, string[]> = {
  'image/jpeg': [],
  'image/png': [],
  'image/gif': [],
  'image/webp': [],
  'video/*': [],
  'application/pdf': [],
  '.doc': [],
  '.docx': [],
  '.xls': [],
  '.xlsx': [],
};

// Add new constant for safe image and video types
export const SafeImageOrVideoTypes: Record<string, string[]> = {
  'image/jpeg': [],
  'image/png': [],
  'image/gif': [],
  'image/webp': [],
  'video/mp4': [],
  'video/quicktime': [], // .mov
  'video/x-msvideo': [], // .avi
  'video/x-ms-wmv': [], // .wmv
  // Add other common/safe video types if needed
};

interface InlineFileUploaderProps<M extends boolean = boolean> {
  accept?: Record<string, string[]>;
  maxFileSizeCaption?: string | React.ReactNode;
  maxFileSize?: number;
  multiple?: M;
  onFileSelect: M extends true
    ? (files: FileWithPath[] | null) => void
    : (file: FileWithPath | null) => void;
  files?: FileWrapper[] | File[];
  hideActiveFiles?: boolean;
  loading?: boolean;
  validateDimensions?: boolean;
  dimensionRequirements?: ImageDimensionRequirements;
}

interface FileWrapper {
  file: File;
  preview: string;
}

function bytesToSize(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function InlineFileUploader<M extends boolean = boolean>({
  accept = SafeDefaultAcceptTypes,
  multiple = true as M,
  maxFileSizeCaption = 'Max file size is 32 MB',
  maxFileSize,
  onFileSelect,
  files,
  hideActiveFiles = false,
  loading = false,
  validateDimensions = false,
  dimensionRequirements,
}: InlineFileUploaderProps<M>): React.ReactNode {
  const [localFiles, setLocalFiles] = useState<FileWrapper[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Extract dimension requirements from caption if not explicitly provided
  const derivedDimensionRequirements = useMemo(() => {
    if (dimensionRequirements) return dimensionRequirements;
    if (maxFileSizeCaption) {
      return extractDimensionsFromCaption(String(maxFileSizeCaption));
    }
    return null;
  }, [dimensionRequirements, maxFileSizeCaption]);

  const activeFiles = useMemo(() => {
    if (!files) return localFiles;
    if (files.length === 0) return [];

    if ('preview' in files[0]) {
      return files as FileWrapper[];
    }

    return (files as File[]).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
  }, [files, localFiles]);

  const setFiles = useCallback(
    (newFiles: FileWrapper[] | ((prev: FileWrapper[]) => FileWrapper[])) => {
      const updatedFiles =
        typeof newFiles === 'function' ? newFiles(activeFiles) : newFiles;

      if (files !== undefined) {
        // If controlled, just notify via onFileSelect
        const rawFiles = updatedFiles.map((f) => f.file);
        (onFileSelect as any)(multiple ? rawFiles : rawFiles[0] ?? null);
      } else {
        // Otherwise use internal state and notify
        setLocalFiles(updatedFiles);
        const rawFiles = updatedFiles.map((f) => f.file);
        (onFileSelect as any)(multiple ? rawFiles : rawFiles[0] ?? null);
      }
    },
    [files, multiple, onFileSelect, activeFiles]
  );

  const handleFilesAfterValidation = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles = acceptedFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));

      if (multiple) {
        setFiles((prevFiles) => [...prevFiles, ...newFiles]);
      } else {
        // If not multiple, only use the first file
        setFiles(newFiles[0] ? [newFiles[0]] : []);
      }
    },
    [multiple, setFiles]
  );

  const validateAndHandleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      // Clear any previous validation errors
      setValidationError(null);

      // If we're not validating dimensions or there are no requirements, just handle the drop
      if (
        !validateDimensions ||
        !derivedDimensionRequirements ||
        acceptedFiles.length === 0
      ) {
        handleFilesAfterValidation(acceptedFiles);
        return;
      }

      // For images, validate dimensions before accepting
      const file = acceptedFiles[0];
      if (file.type.startsWith('image/')) {
        try {
          const result = await validateImageDimensions(
            file,
            derivedDimensionRequirements
          );
          if (!result.valid && result.message) {
            setValidationError(result.message);
            return;
          }

          // If valid, proceed with the file
          handleFilesAfterValidation(acceptedFiles);
        } catch (error) {
          // Log error without using console.error
          setValidationError('Failed to validate image dimensions');
        }
      } else {
        // Non-image files don't need dimension validation
        handleFilesAfterValidation(acceptedFiles);
      }
    },
    [
      validateDimensions,
      derivedDimensionRequirements,
      handleFilesAfterValidation,
    ]
  );

  const handleRemove = useCallback(
    (fileToRemove: FileWrapper) => {
      setFiles((prevFiles) => prevFiles.filter((f) => f !== fileToRemove));
      URL.revokeObjectURL(fileToRemove.preview);
      // Clear validation error when removing a file
      setValidationError(null);
      // Notify parent that file was removed
      if (multiple) {
        const remainingFiles = activeFiles
          .filter((f) => f !== fileToRemove)
          .map((f) => f.file);
        (onFileSelect as any)(
          remainingFiles.length > 0 ? remainingFiles : null
        );
      } else {
        (onFileSelect as any)(null);
      }
    },
    [activeFiles, multiple, onFileSelect, setFiles]
  );

  // Cleanup effect to revoke object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (files?.length && !('preview' in files[0])) {
        activeFiles.forEach((file) => URL.revokeObjectURL(file.preview));
      }
    };
  }, [activeFiles, files]);

  return (
    <Box position="relative">
      <FileDropzone
        accept={accept}
        multiple={multiple}
        caption={maxFileSizeCaption}
        maxSize={maxFileSize}
        onDrop={validateAndHandleDrop}
        onDropRejected={(rejections) => {
          rejections.forEach((rejection) => {
            rejection.errors.forEach((error) => {
              if (error.code === 'file-too-large') {
                toast.error(
                  `File size exceeds the maximum limit of ${bytesToSize(
                    maxFileSize ?? 0
                  )}`
                );
              }
            });
          });
        }}
        disabled={loading}
      />
      {validationError && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          onClose={() => setValidationError(null)}
        >
          {validationError}
        </Alert>
      )}
      {!hideActiveFiles && activeFiles.length > 0 && (
        <Stack spacing={2} mt={2}>
          <Stack
            component="ul"
            spacing={1}
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {activeFiles.map((fileWrapper, index) => {
              const file = fileWrapper.file;
              const extension = file.name.split('.').pop();

              return (
                <Stack
                  component="li"
                  direction="row"
                  key={index}
                  spacing={2}
                  sx={{
                    alignItems: 'center',
                    border: '1px solid var(--mui-palette-divider)',
                    borderRadius: 1,
                    flex: '1 1 auto',
                    p: 1,
                  }}
                >
                  {file.type.startsWith('image/') ? (
                    <Box sx={{ width: 60, height: 60, position: 'relative' }}>
                      <Box
                        component="img"
                        src={fileWrapper.preview}
                        alt={file.name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                  ) : file.type.startsWith('video/') ? (
                    <Box sx={{ width: 60, height: 60, position: 'relative' }}>
                      <Box
                        component="video"
                        src={fileWrapper.preview}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: 1,
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0, 0, 0, 0.4)',
                          borderRadius: 1,
                        }}
                      >
                        <Icon icon={faPlay} />
                      </Box>
                    </Box>
                  ) : (
                    <FileIcon extension={extension} />
                  )}
                  <Box sx={{ flex: '1 1 auto' }}>
                    <Typography variant="subtitle2">{file.name}</Typography>
                    <Typography color="text.secondary" variant="body2">
                      {bytesToSize(file.size)}
                    </Typography>
                  </Box>
                  <Tooltip title="Remove">
                    <IconButton
                      onClick={() => {
                        handleRemove(fileWrapper);
                      }}
                      disabled={loading}
                    >
                      <Icon icon={faX} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      )}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1,
            borderRadius: 1,
          }}
        >
          <CircularProgress size="small" />
        </Box>
      )}
    </Box>
  );
}
